import * as THREE from 'three';
import type { ActionDefinition } from '@/types';
import { DESK_POSITIONS, WHITEBOARD_POSITION, WORKER_DESK_OFFSET_Z } from '@/utils/constants';

/**
 * Data-driven action definitions. Every action is data, not code.
 * This is critical for scaling to 50+ actions later.
 */
export const ACTIONS: Record<string, ActionDefinition> = {
  idle: {
    id: 'idle',
    label: 'Idle',
    icon: '\uD83E\uDDCD',
    target: 'desk',
    targetOffset: new THREE.Vector3(0, 0, WORKER_DESK_OFFSET_Z),
    targetRotation: Math.PI,
    animationClip: 'idle',
    loop: true,
  },
  type: {
    id: 'type',
    label: 'Type',
    icon: '\u2328\uFE0F',
    target: 'desk',
    targetOffset: new THREE.Vector3(0, 0, WORKER_DESK_OFFSET_Z),
    targetRotation: Math.PI,
    animationClip: 'typing',
    loop: true,
    vfx: 'typing-particles',
  },
  write: {
    id: 'write',
    label: 'Write',
    icon: '\u270F\uFE0F',
    target: 'whiteboard',
    targetOffset: new THREE.Vector3(0.3, 0, 0),
    targetRotation: -Math.PI / 2,
    animationClip: 'writing',
    loop: true,
    vfx: 'writing-marker',
  },
  think: {
    id: 'think',
    label: 'Think',
    icon: '\uD83E\uDD14',
    target: 'desk',
    targetOffset: new THREE.Vector3(0, 0, WORKER_DESK_OFFSET_Z),
    targetRotation: Math.PI,
    animationClip: 'thinking',
    loop: true,
  },
};

/**
 * Resolve target position per-worker at runtime.
 * Workers go to THEIR desk, not a fixed global position.
 */
export function resolveTargetPosition(
  action: ActionDefinition,
  deskIndex: number,
): THREE.Vector3 {
  switch (action.target) {
    case 'desk': {
      const deskPos = DESK_POSITIONS[deskIndex];
      return deskPos.position.clone().add(action.targetOffset);
    }
    case 'whiteboard': {
      return WHITEBOARD_POSITION.clone().add(action.targetOffset);
    }
    case 'center': {
      return new THREE.Vector3(deskIndex * 0.5, 0, 1).add(action.targetOffset);
    }
    default:
      return new THREE.Vector3(0, 0, 0);
  }
}

/**
 * Resolve target Y rotation for a given action and desk index.
 * For desk actions, worker faces opposite the desk rotation (toward the monitor).
 */
export function resolveTargetRotation(action: ActionDefinition, deskIndex: number): number {
  if (action.target === 'desk') {
    // Worker faces opposite the desk direction — toward the monitor
    return DESK_POSITIONS[deskIndex].rotation + Math.PI;
  }
  return action.targetRotation;
}
